import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { installStaleChunkReload } from "@seagames/ui";
import { StrictMode } from "react";
import { Toaster } from "react-hot-toast";
import { createRoot } from "react-dom/client";
import "./styles.css";
import { queryClient } from "./lib/api/query-client";
import { onSignOut } from "./lib/auth/token-store";
import { initI18n } from "./lib/i18n";
import { AppUiStrings } from "./lib/i18n/ui-strings";
import { router } from "./router";

// A tab opened before a deploy asks for chunks that are gone: reload once to
// the new version instead of showing an error (before anything lazy loads).
installStaleChunkReload();

// Before the first render, so no frame ever paints in the wrong language.
// English is bundled; a stored non-English locale costs one chunk fetch.
await initI18n();

/**
 * The token store is plain module state, not React state, because the API
 * client needs it outside any component. This is the one bridge back: when the
 * SERVER has ruled on the credential, drop the cache and go to /login.
 *
 * It fires only from `handleAuthFailure` in the API client, which asks
 * `isCredentialVerdict()` and nothing else. A 503 never reaches here.
 */
onSignOut((reason) => {
	queryClient.clear();
	void router.navigate({
		to: "/login",
		search: { reason, redirect: window.location.pathname },
		replace: true,
	});
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("#root is missing from index.html");

createRoot(rootElement).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			{/* @seagames/ui chrome ("Filter", "Rows per page", "Search…") in the viewer's language, for every page at once. */}
			<AppUiStrings>
				<RouterProvider router={router} />
			</AppUiStrings>
			{/* The designer's `.toast`: a navy-ink pill, bottom centre, white 13.5px text, yellow icon. */}
			<Toaster
				position="bottom-center"
				gutter={8}
				// A sticky action bar on the page (the entries scoreboard) raises this over itself.
				containerStyle={{ bottom: "var(--gms-toast-bottom, 6rem)" }}
				toastOptions={{
					duration: 4000,
					style: {
						fontSize: "0.84375rem",
						fontWeight: 500,
						fontFamily: "var(--font-sans)",
						color: "#ffffff",
						background: "var(--color-gms-navy-ink)",
						borderRadius: "0.5rem",
						boxShadow: "0 1rem 2.5rem -1.375rem rgba(9,10,40,0.8)",
						padding: "0.6875rem 1rem",
						maxWidth: "min(26rem, calc(100vw - 2rem))",
					},
					success: { iconTheme: { primary: "var(--color-gms-yellow)", secondary: "var(--color-gms-navy-ink)" } },
					error: { iconTheme: { primary: "var(--color-gms-red-lift)", secondary: "#ffffff" } },
				}}
			/>
		</QueryClientProvider>
	</StrictMode>,
);
